import { ThemeProvider } from '@epilot/concorde-elements'
import { ACTION_TYPE, dispatchExitEvent } from '@epilot/journey-logic-commons'
import type {
  Journey,
  StepState,
  ValueError,
  JourneyRenderFlags,
  HistoryStackState,
  Action
} from '@epilot/journey-logic-commons'
import isEqual from 'fast-deep-equal/react'
import { isObject } from 'radashi'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  NavigationProvider,
  useJourneyContext,
  makeJourneyEmptyStepValuesWithDefaults
} from '../../blocks-renderers'
import { useCustomCSS } from '../../blocks-renderers/utils/hooks/useCustomCSS'
import { useFontInjection } from '../../blocks-renderers/utils/hooks/useFontInjection'
import { useAppStoreSelector } from '../../hooks/useAppStoreSelector'
import { useInitiateProductSelection } from '../../hooks/useInitiateProductSelection'
import { useIsDirty } from '../../hooks/useIsDirty'
import { useStepTransitions } from '../../hooks/useStepTransitions'
import { useSyncAppStore } from '../../hooks/useSyncAppStore'
import { useSyncHiddenBlocksState } from '../../hooks/useSyncHiddenBlocksState'
import { useTheme } from '../../hooks/useTheme'
import {
  SubmissionError,
  submissionListener
} from '../../listeners/submissionListener'
import { journeySubmissionFailed } from '../../store/actions/journeySubmissionFailed'
import { journeySubmissionStarted } from '../../store/actions/journeySubmissionStarted'
import { journeySubmitted } from '../../store/actions/journeySubmitted'
import {
  selectJourney,
  selectSelectedJourneyId
} from '../../store/selectors/config'
import {
  selectAppParamsOrThrows,
  selectCurrentStepOrThrow,
  selectShownBlocks
} from '../../store/selectors/runtime'
import { extractLauncherJourneyLauncherBlock } from '../../utils/extractLauncherJourneyLauncherBlock'
import {
  getBlockTypesForError,
  getHigherPriorityErrorKey
} from '../../utils/journeyErrorUtils'

import { LinearJourney } from './components/LinearJourney'
import type { StepComponentProps } from './components/StepComponent'
import {
  publishExitFullScreenMessage,
  publishEnterFullScreenMessage,
  publishCloseJourneyMessage
} from './embedJourneyPublishers'
import {
  computeDisplayConditionsStatus,
  getValueErrors,
  getWhichBlockHasChanged
} from './utils'

type JourneyPageNextProps = {
  journey: Journey
  launcherJourney: Journey | null
  onJourneyChange: (journey: Journey) => void
  isFocusedOnJourney: boolean
  initialStepValues?: StepState[]
  flags: JourneyRenderFlags
  history: HistoryStackState
  launcherBlockName?: string
  clearCurrentUserSession: () => void
}

export const JourneyPageNext = ({
  journey,
  onJourneyChange,
  isFocusedOnJourney,
  initialStepValues,
  flags,
  history,
  launcherJourney,
  clearCurrentUserSession
}: JourneyPageNextProps) => {
  useSyncAppStore()
  useSyncHiddenBlocksState()

  const { goBack, goNext, animatingBackwards } = useStepTransitions()
  const _currentStep = useAppStoreSelector(selectCurrentStepOrThrow)
  const { context, updateContext } = useJourneyContext()
  const { t } = useTranslation()
  const { mode, preview } = useAppStoreSelector(selectAppParamsOrThrows)
  const activeLinkedJourneyId = useAppStoreSelector(selectSelectedJourneyId)

  const parentJourney = launcherJourney ?? journey
  const activeLinkedJourney = useMemo(
    () =>
      isFocusedOnJourney && journey.journeyId === activeLinkedJourneyId
        ? journey
        : null,
    [activeLinkedJourneyId, isFocusedOnJourney, journey]
  )

  const activeJourney = activeLinkedJourney ?? journey
  const launcherBlockName = useMemo(() => {
    const launcherBlock =
      launcherJourney && extractLauncherJourneyLauncherBlock(launcherJourney)

    return launcherBlock?.name
  }, [launcherJourney])

  const [isFullscreen, setIsFullscreen] = useState(false)

  const journeyV4 = useAppStoreSelector(selectJourney)
  const shownBlocks = useAppStoreSelector(selectShownBlocks)

  if (!journeyV4) {
    // mostly to coerce TS, should never happen
    throw new Error('Journey v4 should always be available in store')
  }

  // used to send InitiateProductSelection event to website
  useInitiateProductSelection(
    activeJourney,
    history.currentIndex,
    history.stack
  )

  const initialStepsForCurrentJourney = useMemo(
    () =>
      makeJourneyEmptyStepValuesWithDefaults(activeJourney, initialStepValues),
    [initialStepValues, activeJourney]
  )

  const isDirty = useIsDirty(
    initialStepsForCurrentJourney,
    context._stepsStateArray,
    journey.journeyId,
    journey.name,
    history.stack
  )

  const stepsErrors =
    context._errors.length === activeJourney.steps.length
      ? context._errors
      : Array.from<unknown, ValueError[]>(
          { length: activeJourney.steps.length },
          () => []
        )

  const currentStepIndex = _currentStep.orderIndex
  const stepsState = context._stepsStateArray
  const currentStep = activeJourney.steps[currentStepIndex] || {}
  const currentStepErrors = useMemo(
    () => stepsErrors[currentStepIndex] ?? [],
    [currentStepIndex, stepsErrors]
  )

  /**
   * Gets called prior to navigating to a new step,
   * if there are errors for the current step.
   */
  const updateJourneyContextWithError = useCallback(() => {
    const erroredBlockTypes = getBlockTypesForError(
      currentStepErrors,
      activeJourney,
      currentStepIndex
    )

    /**
     * Undefined error blocks -> generic error
     * 1 error block -> try to get the error key specific for the block
     * More than one error blocks -> show higher priority error or generic one
     */
    const errorMessageTranslationKey: string = getHigherPriorityErrorKey(
      erroredBlockTypes || undefined
    )

    updateContext((context) => ({
      ...context,
      errorValidationMode: 'ValidateAndShow',
      _errors: [...stepsErrors],
      errorMessageTranslationKey,
      erroredStepIndex: currentStepIndex
    }))
  }, [
    activeJourney,
    currentStepErrors,
    currentStepIndex,
    stepsErrors,
    updateContext
  ])

  /* If navigating within a launcher journey, we might need to enter or exit  */
  useEffect(
    function triggerFullscreenOnActiveLinkedJourney() {
      if (flags.isPreview || !launcherJourney) {
        return
      }

      const { journeyId } = launcherJourney
      const initialStepIndex = context._linkedJourneyInitialStepIndex ?? 0

      if (isFullscreen && currentStepIndex === initialStepIndex) {
        /* If we're in fullscreen and we're going back, we need to exit */
        setIsFullscreen(false)
        publishExitFullScreenMessage({
          journeyId,
          isLauncherJourney: true
        })
      } else if (!isFullscreen && currentStepIndex !== initialStepIndex) {
        /* If we're not in fullscreen and we're not going back, we need to enter */
        setIsFullscreen(true)
        publishEnterFullScreenMessage({
          journeyId,
          isLauncherJourney: true
        })
      }
    },
    [
      context._linkedJourneyInitialStepIndex,
      currentStepIndex,
      flags.isPreview,
      isFocusedOnJourney,
      isFullscreen,
      launcherJourney,
      parentJourney
    ]
  )

  // updating initial State
  /**
   * Will only be executed on first render.
   * @todo Review its' purpose
   */
  useEffect(() => {
    if (initialStepValues && !isEqual(initialStepValues, stepsState)) {
      updateContext((prevState) => ({
        ...prevState,
        journeyStepStateMap: {
          ...prevState.journeyStepStateMap,
          [journey.journeyId]: initialStepValues
        }
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onStepStateChange = async ({
    newState,
    stepIndex,
    valueErrors
    // triggerBlockNames,
    // isAfterNavigation
  }: {
    newState: StepState | StepState[]
    stepIndex: number
    valueErrors?: ValueError[]
    triggerBlockNames?: string[]
    isAfterNavigation?: boolean
  }) => {
    const updatedJourney: Journey | undefined = undefined
    let tempStepsState = [...stepsState]
    const errors = [...stepsErrors]

    if (Array.isArray(newState)) {
      tempStepsState = [...newState]
    } else {
      // Only dispatch event when the data is changed by the user
      window.dispatchEvent(
        new CustomEvent('journey-state-update', {
          detail: { newState, stepIndex }
        })
      )
      tempStepsState[stepIndex] = { ...newState }
    }

    if (valueErrors && !isEqual(errors[currentStepIndex], valueErrors)) {
      errors[currentStepIndex] = valueErrors
    }

    updateContext((context) => {
      const journeyId = journey.journeyId

      return {
        ...context,
        _errors: errors,
        journeyStepStateMap: {
          ...context.journeyStepStateMap,
          [journeyId]: tempStepsState.map((element) => element ?? {})
        }
      }
    })

    if (updatedJourney) {
      onJourneyChange(updatedJourney)
    }
  }

  const handleOnClose = useCallback(() => {
    dispatchExitEvent()
    publishCloseJourneyMessage({
      journeyId: parentJourney.journeyId,
      isDirty
    })
  }, [isDirty, parentJourney.journeyId])

  const handleChange: StepComponentProps['onChange'] = (value) => {
    const { data } = value

    /**
     * If step journey launcher is being rendered, the step's newState
     * will be contained in the key named after the launcher block
     */
    const newState: StepState =
      launcherBlockName && isObject(data) && launcherBlockName in data
        ? isObject(data[launcherBlockName]) && 'data' in data[launcherBlockName]
          ? data[launcherBlockName].data
          : data[launcherBlockName]
        : data

    const valueErrors = getValueErrors(
      value,
      currentStepIndex,
      newState,
      currentStep
    )

    const currentStepState = stepsState[currentStepIndex] ?? {}

    if (newState && !isEqual(newState, currentStepState)) {
      /* If state has changed */
      // handle navigation from Step events
      const triggerBlockNames = getWhichBlockHasChanged(
        currentStepState,
        newState
      )

      onStepStateChange({
        newState,
        stepIndex: currentStepIndex,
        valueErrors,
        triggerBlockNames
      })
    } else if (!isEqual(stepsErrors[currentStepIndex] ?? [], valueErrors)) {
      /* If state has not changed but errors have changed */
      const errors = [...stepsErrors]

      errors[currentStepIndex] = valueErrors
      updateContext((context) => ({
        ...context,
        _errors: errors
      }))
    }
  }

  const { concordeTheme, designTokens, darkMode } = useTheme()

  // Handle custom css
  useCustomCSS(journey, designTokens?.custom_css)

  /* Inject fonts */
  useFontInjection(concordeTheme.typography.fontSource)

  const handleNavigation = useCallback(
    async (action: Action) => {
      switch (action.actionType) {
        case ACTION_TYPE.GO_BACK:
          return void goBack()
        case ACTION_TYPE.GO_NEXT: {
          // Navigation to next step should be blocked if there are errors on this step
          if (currentStepErrors.length > 0) {
            updateJourneyContextWithError()

            return
          }

          return void goNext(action.targetStepId)
        }
        case ACTION_TYPE.SUBMIT_GO_NEXT: {
          // Submission should be blocked if there are errors
          if (currentStepErrors.length > 0) {
            updateJourneyContextWithError()

            return
          }

          // Prevent submission when in preview mode
          if (flags.isPreview) return

          journeySubmissionStarted(undefined)

          updateContext((currentContext) => ({
            ...currentContext,
            submissionErrorKey: undefined
          }))

          const displayConditionsStatus = computeDisplayConditionsStatus(
            journeyV4,
            shownBlocks
          )

          try {
            await submissionListener(
              activeJourney,
              stepsState,
              history,
              context,
              displayConditionsStatus,
              t,
              clearCurrentUserSession
            )

            journeySubmitted(undefined)
            goNext(action.targetStepId)
          } catch (error) {
            journeySubmissionFailed(undefined)

            if (error instanceof SubmissionError) {
              updateContext((currentContext) => ({
                ...currentContext,
                submissionErrorKey: (error as SubmissionError).errorKey
              }))
            }
          }
          break
        }
      }
    },
    [
      goBack,
      currentStepErrors.length,
      goNext,
      updateJourneyContextWithError,
      flags.isPreview,
      updateContext,
      journeyV4,
      shownBlocks,
      activeJourney,
      stepsState,
      history,
      context,
      t,
      clearCurrentUserSession
    ]
  )

  return (
    <ThemeProvider
      darkMode={darkMode}
      designTokens={designTokens}
      theme={concordeTheme}
    >
      <NavigationProvider
        isNavigatingBack={animatingBackwards}
        onAction={handleNavigation}
      >
        <LinearJourney
          currentStepIndex={currentStepIndex}
          debug={flags.debug}
          isFocusedOnJourney={isFocusedOnJourney}
          isLinearJourney={flags.isLinear}
          isParentLauncherJourney={Boolean(launcherJourney)}
          isPreview={preview}
          journey={journey}
          mode={mode}
          onChange={handleChange}
          onCloseButtonClicked={handleOnClose}
          onGoBack={goBack}
          onNavigateToStep={() => void 0}
          parentJourneyId={launcherJourney?.journeyId}
          showCloseButton={flags.showCloseButton}
          showTopBar={flags.showTopBar}
          stepsErrors={stepsErrors}
          stepsState={stepsState}
        />
      </NavigationProvider>
    </ThemeProvider>
  )
}

JourneyPageNext.displayName = 'JourneyPage (Next)'
